import React, { useEffect, useRef, useState } from 'react';

//Constants
import { LABELS, RESUME_Tab_INDEX } from '../constants/general';
import { ProfileByResumeValidationSchema } from '../constants/schemas';
//Utils
import { classNames } from 'primereact/utils';
import { getDataFromResume } from '../middleware/resumeDetails';
import { getFormErrorMessage, isFormFieldValid } from '../utils/general';
//Components
import EducationDetailForm from './EducationDetailForm';
import EmploymentDetailForm from './EmploymentDetailForm';
import ProjectDetailForm from './ProjectDetailForm';
import UserDetailForm from './UserDetailForm';
//Utility Packages
import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { TabPanel, TabView } from 'primereact/tabview';
import Label from './Label';
//Redux
import { useSelector } from 'react-redux';
import { dispatch } from '../utils/store';
import { Actions } from '../redux/actions';
//Images
import uploadIcon from '../assets/images/grid-upload-icon.svg';

const FillProfileByResume = ({ show, getProfileData, onHide }) => {
  // Store
  const { resumeData } = useSelector((state) => state.resumeFetchDetails);
  const toastReff = useSelector((state) => state.notifications.toastReff);
  //State
  const [isEditable, setIsEditable] = useState({
    UserDetailForm: false,
    ProjectDetailForm: false,
    EducationDetailForm: false,
    EmploymentDetailForm: false
  });
  const [fileName, setFileName] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  // React useRef hooks.
  const fileRef = useRef(null);

  const formik = useFormik({
    initialValues: {
      file: null
    },
    validationSchema: ProfileByResumeValidationSchema,
    onSubmit: async (values) => {
      const payload = new FormData();
      payload.append('resume', values.file);
      const data = await getDataFromResume(payload, toastReff);
      dispatch(Actions.ResumeFetchDetails.ResetAllDetails);
      data
        ? dispatch(Actions.ResumeFetchDetails.SetResumeData, data)
        : dispatch(Actions.ResumeFetchDetails.ResetAllData);
      setFileName('');
      formik.resetForm();
      if (fileRef.current) {
        fileRef.current.value = null;
      }
      setTabIndex(0);
      setIsEditable({
        UserDetailForm: false,
        ProjectDetailForm: false,
        EducationDetailForm: false,
        EmploymentDetailForm: false
      });
    }
  });
  useEffect(() => {
    return () => {
      dispatch(Actions.ResumeFetchDetails.ResetAllData);
    };
  }, []);

  const handleClosePopup = () => {
    dispatch(Actions.ResumeFetchDetails.ResetAllData);
    setFileName('');
    formik.resetForm();
    setTabIndex(0);
    if (isEditable.UserDetailForm) getProfileData();
    setIsEditable({
      UserDetailForm: false,
      ProjectDetailForm: false,
      EducationDetailForm: false,
      EmploymentDetailForm: false
    });
    onHide();
  };

  const renderHeader = () => {
    return <div className="fill-profile-header">Upload Profile by Resume</div>;
  };
  return (
    <Dialog
      header={renderHeader}
      visible={show}
      onHide={handleClosePopup}
      modal
      className={
        resumeData && Object.keys(resumeData)?.length !== 0
          ? 'p-fluid fill-profile-modal resume-data-modal'
          : 'p-fluid fill-profile-modal'
      }>
      <div className="mb-5">
        <form onSubmit={formik.handleSubmit} className="resume-container p-fluid">
          <div className="form-row-wrapper">
            <div className="form-col">
              <div className="form-group-outer upload-document-wrapper">
                <div className="custom-form-group">
                  <Label htmlFor="file" text="Resume" isBold isMandatory />
                  <input
                    hidden
                    ref={fileRef}
                    type="file"
                    id="file"
                    name="file"
                    onChange={(e) => {
                      formik.setFieldValue('file', e.target?.files[0] || null);
                      setFileName(e.target?.files[0]?.name);
                    }}
                    accept=".doc, .docx, .pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className={classNames({
                      'p-invalid': isFormFieldValid('file')
                    })}
                  />
                </div>
                <div className="resume-info-wrapper">
                  <Button
                    text
                    type="button"
                    className="gray-btn upload-btn"
                    onClick={() => {
                      fileRef.current.click();
                    }}>
                    <div>
                      <em>
                        <img src={uploadIcon} alt="upload icon" />
                      </em>
                      <span>{LABELS.INPUT.RESUME_UPLOAD}</span>
                    </div>
                  </Button>
                  {getFormErrorMessage(formik, 'file')}
                  <p>{fileName}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="submit-btn-wrapper">
            <Button
              type="button"
              variant="contained"
              className="ims-blue-btn"
              onClick={formik.handleSubmit}>
              {LABELS.BUTTON.UPLOAD}
            </Button>
          </div>
        </form>
      </div>
      {resumeData && Object.keys(resumeData)?.length !== 0 && (
        <TabView
          activeIndex={tabIndex}
          onTabChange={(e) => setTabIndex(e.index)}
          className="custom-tabbing">
          <TabPanel header="User Details">
            {tabIndex === RESUME_Tab_INDEX['User Details'] && (
              <UserDetailForm
                isEditable={isEditable.UserDetailForm}
                setIsEditable={(value) =>
                  setIsEditable((prevValue) => ({ ...prevValue, UserDetailForm: value }))
                }
              />
            )}
          </TabPanel>
          {resumeData?.Projects && resumeData?.Projects.length && (
            <TabPanel header="Project Details">
              {tabIndex === RESUME_Tab_INDEX['Project Details'] && (
                <ProjectDetailForm
                  isEditable={isEditable.ProjectDetailForm}
                  setIsEditable={(value) =>
                    setIsEditable((prevValue) => ({ ...prevValue, ProjectDetailForm: value }))
                  }
                />
              )}
            </TabPanel>
          )}
          {resumeData?.Education && resumeData?.Education.length && (
            <TabPanel header="Education Details">
              {tabIndex === RESUME_Tab_INDEX['Education Details'] && (
                <EducationDetailForm
                  isEditable={isEditable.EducationDetailForm}
                  setIsEditable={(value) =>
                    setIsEditable((prevValue) => ({ ...prevValue, EducationDetailForm: value }))
                  }
                />
              )}
            </TabPanel>
          )}
          {resumeData?.Experience && resumeData?.Experience.length && (
            <TabPanel header="Employment Details">
              {tabIndex === RESUME_Tab_INDEX['Employment Details'] && (
                <EmploymentDetailForm
                  isEditable={isEditable.EmploymentDetailForm}
                  setIsEditable={(value) =>
                    setIsEditable((prevValue) => ({ ...prevValue, EmploymentDetailForm: value }))
                  }
                />
              )}
            </TabPanel>
          )}
        </TabView>
      )}
    </Dialog>
  );
};

export default FillProfileByResume;
